import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { Page, Project, ProjectForm, DateInterval, IdName } from "../models";
import { ProjectMarkerDto } from "../models/project-marker.dto";

export type MarkerFilter = {
  status?: string;
  typeId?: number | null;
  siteId?: number | null;
  commercialId?: number | null;
  page?: number;
  size?: number;
};

@Injectable()
export class ProjectService {
  projectsUrl = environment.dns + "api/projects/";

  projects: Project[] = [];

  constructor(private http: HttpClient) {}

  getPaginate(page: number, size: number, search: string = ""): Observable<Page<Project>> {
    return this.http.get<Page<Project>>(
      this.projectsUrl + "paginate?page=" + page + "&size=" + size + "&search=" + search
    );
  }

  getList(): Observable<Project[]> {
    return this.http.get<Project[]>(this.projectsUrl + "list");
  }

  getOne(id: number): Observable<Project> {
    return this.http.get<Project>(this.projectsUrl + id);
  }

  getStatistics(dateInterval: DateInterval): Observable<any[]> {
    return this.http.post<any[]>(this.projectsUrl + "statistics", dateInterval);
  }

  getStatisticsByCommercial(dateInterval: DateInterval): Observable<any[]> {
    return this.http.post<any[]>(this.projectsUrl + "commercials/statistics", dateInterval);
  }

  getStatisticsBySite(dateInterval: DateInterval): Observable<any[]> {
    return this.http.post<any[]>(this.projectsUrl + "sites/statistics", dateInterval);
  }

  add(projectForm: ProjectForm): Observable<Project> {
    return this.http.post<Project>(this.projectsUrl, projectForm);
  }

  update(projectForm: ProjectForm): Observable<Project> {
    return this.http.put<Project>(this.projectsUrl, projectForm);
  }

  delete(id: number): Observable<boolean> {
    return this.http.delete<boolean>(this.projectsUrl + id);
  }

  deleteMultiple(projectsId: number[]): Observable<boolean> {
    return this.http.delete<boolean>(this.projectsUrl + "multiple/" + projectsId);
  }

  getMapMarkers(): Observable<ProjectMarkerDto[]> {
    return this.http.get<ProjectMarkerDto[]>(this.projectsUrl + "map/markers");
  }

  getMapMarkersInBbox(
    minLat: number,
    minLon: number,
    maxLat: number,
    maxLon: number,
    opts: MarkerFilter = {}
  ): Observable<Page<ProjectMarkerDto>> {
    let params = new HttpParams()
      .set("minLat", String(minLat))
      .set("minLon", String(minLon))
      .set("maxLat", String(maxLat))
      .set("maxLon", String(maxLon));

    if (opts.status) params = params.set("status", opts.status);
    if (opts.typeId != null) params = params.set("typeId", String(opts.typeId));
    if (opts.siteId != null) params = params.set("siteId", String(opts.siteId));
    if (opts.commercialId != null) params = params.set("commercialId", String(opts.commercialId));

    const page = opts.page != null ? opts.page : 0;
    const size = opts.size != null ? opts.size : 1000;

    params = params.set("page", String(page));
    params = params.set("size", String(size));

    return this.http.get<Page<ProjectMarkerDto>>(this.projectsUrl + "map", { params });
  }

  searchMin(q: string, page = 0, size = 20): Observable<Page<IdName>> {
    return this.http.get<Page<IdName>>(
      this.projectsUrl + "search?q=" + encodeURIComponent(q || "") + "&page=" + page + "&size=" + size
    );
  }
}
